import React, { useRef } from 'react';
import { Pencil, Trash2, Upload, Eye } from 'lucide-react';
import type { Invoice } from '../../../../types/subscriptionManagement';

interface Props {
  invoices: Invoice[];
  onEdit: (inv: Invoice) => void;
  onDelete: (inv: Invoice) => void;
  onUpload: (id: string, file: File) => void;
  onViewFile: (url: string) => void;
}

function fmtAmt(n: number | string, currency?: string): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const prefix = currency === 'INR' ? '₹' : currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${prefix}${v.toLocaleString('en-US')}`;
}

function fmtDate(d: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const UploadCell: React.FC<{
  invoice: Invoice;
  onUpload: (id: string, file: File) => void;
  onView: (url: string) => void;
}> = ({ invoice, onUpload, onView }) => {
  const fileRef = useRef<HTMLInputElement>(null);

  if (invoice.invoiceFile) {
    return (
      <button
        type="button"
        onClick={() => onView(invoice.invoiceFile)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
      >
        <Eye size={13} /> View
      </button>
    );
  }

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,image/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onUpload(invoice.id, file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-600"
      >
        <Upload size={13} /> Upload
      </button>
    </>
  );
};

const HEADERS = [
  'ID',
  'Subscription ID',
  'Invoice #',
  'Date',
  'Amount',
  'Currency',
  'Payment method',
  'Status',
  'File',
  'Actions',
] as const;

export const InvoiceTable: React.FC<Props> = ({ invoices, onEdit, onDelete, onUpload, onViewFile }) => {
  if (invoices.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
        <p className="text-slate-500 text-sm">No invoices yet. Click &quot;Add Invoice&quot; to record one.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {HEADERS.map(h => (
                <th
                  key={h}
                  className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors group">
                <td className="px-3 py-2.5 text-[12px] font-mono text-slate-700">{inv.id || '—'}</td>
                <td className="px-3 py-2.5 text-[12px] font-mono text-slate-600">{inv.subscriptionId || '—'}</td>
                <td className="px-3 py-2.5 text-[13px] font-semibold text-slate-800">{inv.invoiceNumber || '—'}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600">{fmtDate(inv.invoiceDate)}</td>
                <td className="px-3 py-2.5 text-[12px] font-medium text-slate-700 whitespace-nowrap">
                  {fmtAmt(inv.amount, inv.currency)}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600">{inv.currency || '—'}</td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600 max-w-[140px] truncate" title={inv.paymentMethod}>
                  {inv.paymentMethod?.trim() || '—'}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600">{inv.paymentStatus || '—'}</td>
                <td className="px-3 py-2.5">
                  <UploadCell invoice={inv} onUpload={onUpload} onView={onViewFile} />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => onEdit(inv)}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(inv)}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
